import { useEffect, useRef, type ReactNode } from 'react'
import { useQueryClient, onlineManager } from '@tanstack/react-query'

import { useAuth } from '@/shared/contexts/AuthContext'
import { useConnectionSetter } from '@/shared/contexts/ConnectionContext'
import { serverReachability } from '@/shared/lib/server-reachability'
import { handleSSEEvent } from '@/features/notifications/sse-handler'
import type { SSEEvent } from '@app/shared/types/events'

type CompetitionSSEProviderProps = {
  competitionId: string
  children: ReactNode
}

/** Manages an SSE connection for a competition. Place inside layouts that need real-time updates. */
export function CompetitionSSEProvider({
  competitionId,
  children,
}: CompetitionSSEProviderProps): React.ReactElement {
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { setConnectionStatus } = useConnectionSetter()
  const userRef = useRef(user)
  userRef.current = user

  useEffect(() => {
    let eventSource: EventSource | null = null

    function connect(): void {
      eventSource?.close()
      const url = `/api/notifications/stream?competitionId=${encodeURIComponent(competitionId)}`
      eventSource = new EventSource(url)

      eventSource.onopen = () => {
        // SSE connected = server is definitely reachable
        serverReachability.reportSuccess()
        setConnectionStatus('connected')
      }

      eventSource.onerror = () => {
        // SSE failed = server may be unreachable, trigger health check
        serverReachability.reportFailure()
        setConnectionStatus('reconnecting')
      }

      eventSource.onmessage = (e) => {
        try {
          const event = JSON.parse(e.data) as SSEEvent
          handleSSEEvent(event, queryClient, userRef.current)
        } catch {
          console.error('Failed to parse SSE event:', e.data)
        }
      }
    }

    // Connect only if TQ is online (gate is open, sync is done).
    // This ensures SSE events don't invalidate cache during sync.
    if (onlineManager.isOnline()) {
      connect()
    }

    // Reconnect when TQ goes online (which is after sync completes)
    const unsubOnline = onlineManager.subscribe((isOnline) => {
      if (isOnline) {
        connect()
      } else {
        eventSource?.close()
        eventSource = null
      }
    })

    return () => {
      unsubOnline()
      eventSource?.close()
    }
  }, [competitionId, queryClient, setConnectionStatus])

  return <>{children}</>
}
